import { Auth, Hub } from 'aws-amplify';
import React from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import { AppBannersProvider } from './components/alliance/app-banners';
import { AllianceErrorBoundary } from './components/alliance/error-boundary';
import { CrashScreen } from './components/alliance/system-screens';
import { DevCookieBtn } from './components/dev-cookie-btn';
import { PurchaseNoAccess } from './components/purchase-no-access';
import { RedirectSignIn } from './components/redirect-sign-in';
import { RouteLoading } from './components/route-loading';
import { AppProvider } from './contexts/app-context';
import { AuthContext } from './contexts/auth-context';
import { usePermissions } from './hooks/use-permissions';
import { Event } from './routes/event';
import { Events } from './routes/events';
import { Home } from './routes/home';
import { Hotspots } from './routes/hotspots';
import { NotFound } from './routes/not-found';
import { Purchase } from './routes/purchase';
import { Root } from './routes/root';
import { Trip } from './routes/trip';
import { Trips } from './routes/trips';
import { captureError } from './utils/capture-error';
import { AgtStaffPermissions, AWS_GROUP } from './types';

const AppRoutes = {
  Account: React.lazy(() => import('./routes/account/account-routes')),
  Admin: React.lazy(() => import('./routes/admin/admin-routes')),
  Ticket: React.lazy(() => import('./routes/ticket')),
  Print: React.lazy(() => import('./routes/print')),
  Registration: React.lazy(() => import('./routes/registration')),
  Waiver: React.lazy(() => import('./routes/waiver')),
};

export function App() {
  // Context
  const { state, dispatch } = React.useContext(AuthContext);
  // Hooks
  const { checkPermissions } = usePermissions();

  React.useEffect(() => {
    // Amplify has a local eventing system called Hub
    // Amplify’s Auth category publishes in the auth channel
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          dispatch({ type: 'USER_SET', payload: data });
          break;
        case 'tokenRefresh_failure':
          dispatch({ type: 'TOKEN_ERROR' });
          break;
        case 'signOut':
          dispatch({ type: 'USER_CLEAR' });
          break;
      }
    });

    const checkForUser = async () => {
      try {
        dispatch({ type: 'SET_CHECKING', payload: true });

        // Used to check if a user is logged in when the page is loaded
        const user = await Auth.currentAuthenticatedUser();
        dispatch({ type: 'USER_SET', payload: user });

        // Provides decoded JWT Tokens
        const userSession = await Auth.currentSession();
        dispatch({ type: 'SESSION_SET', payload: userSession });
      } catch (error) {
        // Will throw an error if there is no user logged in
        dispatch({ type: 'USER_CLEAR' });
      } finally {
        dispatch({ type: 'SET_CHECKING', payload: false });
      }
    };

    checkForUser();
  }, [dispatch]);

  const isAuthenticated = state.user !== undefined;
  const isAdmin = checkPermissions([AWS_GROUP.AllianceEvents_Admin]);

  const isAgtStaff = state.session?.getAccessToken().decodePayload()[
    'myStaffForAgt'
  ];
  const staffPerms =
    state.session?.getAccessToken().decodePayload()['cognito:groups'] || [];
  const canPurchase = isAgtStaff
    ? staffPerms.includes(AgtStaffPermissions['Wallet'])
    : true;

  // Staff without the Wallet permission can't buy or register
  const noPurchase = <PurchaseNoAccess />;

  return (
    // The last line of defence: a crash outside any route boundary (the shell
    // itself) shows the full-page crash screen instead of a blank page
    <AllianceErrorBoundary
      onError={(error, errorInfo) =>
        captureError({ data: { error, errorInfo } })
      }
      fallback={({ reset }) => (
        <CrashScreen
          layout="page"
          onRetry={reset}
          onHome={() => window.location.assign('/')}
        />
      )}
    >
      <AppProvider>
        <DevCookieBtn />

        <AppBannersProvider>
          <Router>
            <Routes>
              <Route path="/" element={<Root />}>
                <Route index element={<Home />} />
                {/* The old checkout's URL, kept so saved links still land */}
                <Route
                  path="events/:eventId/purchase"
                  element={
                    <Navigate replace to="../register" relative="path" />
                  }
                />
                <Route
                  path="events/:eventId/register"
                  element={canPurchase ? <Purchase /> : noPurchase}
                />
                <Route path="events/:eventId" element={<Event />} />
                <Route path="events" element={<Events />} />
                <Route path="trips/:tripId" element={<Trip />} />
                <Route path="trips" element={<Trips />} />
                <Route path="hotspots" element={<Hotspots />} />
                <Route
                  path="ticket/:ticketId"
                  element={
                    <React.Suspense fallback={<RouteLoading />}>
                      <AppRoutes.Ticket />
                    </React.Suspense>
                  }
                />
                <Route
                  path="waiver"
                  element={
                    <React.Suspense fallback={<RouteLoading />}>
                      <AppRoutes.Waiver />
                    </React.Suspense>
                  }
                />
                <Route
                  path="registration"
                  element={
                    <React.Suspense fallback={<RouteLoading />}>
                      <AppRoutes.Registration />
                    </React.Suspense>
                  }
                />
                <Route
                  path="account/*"
                  element={
                    isAuthenticated ? (
                      <React.Suspense fallback={<RouteLoading />}>
                        <AppRoutes.Account />
                      </React.Suspense>
                    ) : (
                      <RedirectSignIn />
                    )
                  }
                />
                <Route
                  path="admin/*"
                  element={
                    isAdmin ? (
                      <React.Suspense fallback={<RouteLoading />}>
                        <AppRoutes.Admin />
                      </React.Suspense>
                    ) : isAuthenticated ? (
                      <Navigate replace to="/account" />
                    ) : (
                      <RedirectSignIn />
                    )
                  }
                />
                <Route
                  path="print"
                  element={
                    isAdmin ? (
                      <React.Suspense fallback={<RouteLoading />}>
                        <AppRoutes.Print />
                      </React.Suspense>
                    ) : isAuthenticated ? (
                      <Navigate replace to="/" />
                    ) : (
                      <RedirectSignIn />
                    )
                  }
                />
                {/* Unknown pages show the 404 in the shell instead of going home */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Router>
        </AppBannersProvider>
      </AppProvider>
    </AllianceErrorBoundary>
  );
}
